import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, Select, TextArea } from '@/components';

import { HydrogenProjectTechnology } from '@/features/projects/types';

import { nullableStringSetter } from '@/helpers';
import { useDevelopmentStatus } from '@/hooks';

export type HydrogenFormFields = {
  installed_capacity: string | null;
  development_status: string | null;
  information: string | null;
};

export type HydrogenSubmitFields = {
  installed_capacity: string | null;
  development_status: string | null;
  information: string | null;
};

export type HydrogenFormProps = {
  readonly id: string;
  readonly item: HydrogenProjectTechnology;
  readonly onSubmit: (data: HydrogenSubmitFields) => void;
};

export function HydrogenFormNew({ id, item, onSubmit }: HydrogenFormProps) {
  const { t } = useTranslation(['projects']);
  const { options: developmentStatusOptions } = useDevelopmentStatus();

  const options = useMemo(
    () => ({
      defaultValues: {
        installed_capacity: item.installed_capacity ?? null,
        development_status: item.development_status ?? null,
        information: item.information ?? null,
      },
    }),
    [item]
  );

  const onSubmitHydrogenForm = useCallback(
    (data: HydrogenSubmitFields) => {
      const payload = {
        installed_capacity: data.installed_capacity,
        development_status: data.development_status,
        information: data.information,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<HydrogenFormFields> id={id} onSubmit={(data) => onSubmitHydrogenForm(data)} options={options}>
      {({ formState, register, watch }) => (
        <>
          <div className="mb-6">
            <Field
              id="installed_capacity"
              label={t('Installed Capacity')}
              error={formState.errors['installed_capacity']}
              delay={0}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                id="installed_capacity"
                type="number"
                suffix="MW"
                placeholder={t('Example: 300')}
                color={formState.errors['installed_capacity'] ? 'error' : 'primary'}
                autoComplete="off"
                registration={register('installed_capacity', {
                  ...NUMERIC,
                })}
              />
            </Field>
            <Field
              id="comments"
              label={t('Development status')}
              error={formState.errors['development_status']}
              delay={0.45}
            >
              <Select
                color="primary"
                registration={register('development_status')}
                options={developmentStatusOptions}
                className="mt-2"
              />
            </Field>
          </div>
          <Field
            id="information"
            label={t('Information')}
            error={formState.errors['information']}
            delay={0.6}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="information"
              color={formState.errors['information'] ? 'error' : 'primary'}
              registration={register('information', { setValueAs: nullableStringSetter })}
              className="min-h-32"
            />
          </Field>
        </>
      )}
    </Form>
  );
}
